// The rows not read yet between a conversation's head and the rest, after a
// jump to the head (a tap on the status bar, a far tick of the outline). The
// same for every conversation drawn in a `MessageColumn` (the Agent chat, a
// Topic, a direct message): only the page jumped to is read, and the pages
// between come as the person scrolls down to them.
//
// Promise:
// - A jump reads one page from where it lands, not every page between.
// - Coming near the gap reads its next page from the top, one at a time.
// - A page read for the gap goes in only once the scroll rests (no scroll
//   event for a moment, no finger down): iOS overwrites a scrollTop written
//   during a fling, and the row above would have run away.
// - The row above the gap keeps its place when a page goes in under it (the
//   column runs bottom up and would move it).
import { useMutation } from "@tanstack/react-query";
import { m } from "@trameria/i18n";
import { useEffect, useLayoutEffect, useRef } from "react";

/** Not read: `after` is the last seq read above it, `before` the first below. */
export interface ColumnGap {
  after: number;
  before: number;
}

interface Seq {
  seq: number;
}

/**
 * A page read from a jump's landing, put before the loaded rows (`items`,
 * seq order). Null when it brings nothing before them.
 */
export function putJumpPage<T extends Seq>(
  items: readonly T[],
  page: readonly T[],
): { items: T[]; hasOlder: boolean; gap: ColumnGap | null } | null {
  const top = items[0];
  if (!top) return null;
  const head = page.filter((e) => e.seq < top.seq);
  const first = head[0];
  const last = head.at(-1);
  if (!first || !last) return null;
  return {
    items: [...head, ...items],
    hasOlder: first.seq > 1,
    gap: last.seq < top.seq - 1 ? { after: last.seq, before: top.seq } : null,
  };
}

/**
 * A page read from the top of a gap (`page`: from `gap.after` on, at most
 * `pageSize`), put into it. The gap is null once it is closed.
 */
export function putGapPage<T extends Seq>(
  items: readonly T[],
  gap: ColumnGap,
  page: readonly T[],
  pageSize: number,
): { items: T[]; gap: ColumnGap | null } {
  const fill = page.filter((e) => e.seq > gap.after && e.seq < gap.before);
  const at = items.findIndex((e) => e.seq >= gap.before);
  const next =
    at < 0
      ? [...items, ...fill]
      : [...items.slice(0, at), ...fill, ...items.slice(at)];
  const reached = fill.at(-1)?.seq ?? gap.before - 1;
  return {
    items: next,
    gap:
      page.length < pageSize || reached >= gap.before - 1
        ? null
        : { after: reached, before: gap.before },
  };
}

/** Quiet this long, the scroll has come to rest. */
const REST_MS = 150;

/** Resolves once the pane has not scrolled for a moment and no finger is on it. */
function scrollRest(pane: HTMLElement): Promise<void> {
  return new Promise((resolve) => {
    let timer: number | undefined;
    let touching = false;
    const done = () => {
      pane.removeEventListener("scroll", arm);
      pane.removeEventListener("touchstart", down);
      window.removeEventListener("touchend", up);
      window.removeEventListener("touchcancel", up);
      resolve();
    };
    function arm() {
      window.clearTimeout(timer);
      if (!touching) timer = window.setTimeout(done, REST_MS);
    }
    function down() {
      touching = true;
      window.clearTimeout(timer);
    }
    function up() {
      touching = false;
      arm();
    }
    pane.addEventListener("scroll", arm, { passive: true });
    pane.addEventListener("touchstart", down, { passive: true });
    window.addEventListener("touchend", up, { passive: true });
    window.addEventListener("touchcancel", up, { passive: true });
    arm();
  });
}

export interface ColumnGapFill {
  loading: boolean;
  failed: boolean;
  near(el: HTMLElement, pane: HTMLElement): void;
}

/**
 * Fills a gap a page at a time. `read` reads the next page and answers how
 * to put it in (null: nothing to put); `data` is what the rows are drawn
 * from, to keep the row above in place once they are.
 */
export function useColumnGap(
  read: () => Promise<(() => void) | null>,
  data: unknown,
): ColumnGapFill {
  const anchor = useRef<{
    el: Element;
    top: number;
    pane: HTMLElement;
  } | null>(null);
  const fill = useMutation({
    mutationFn: async ({
      el,
      pane,
    }: {
      el: HTMLElement;
      pane: HTMLElement;
    }) => {
      const put = await read();
      if (!put) return;
      await scrollRest(pane);
      const above = el.previousElementSibling ?? el;
      anchor.current = {
        el: above,
        top: above.getBoundingClientRect().top,
        pane,
      };
      put();
    },
  });
  // frontend-development: not a fetch; keeps the row above a filled gap where it was before the page went in.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when the page went in (`data`), after it is drawn
  useLayoutEffect(() => {
    const a = anchor.current;
    if (!a || fill.isPending) return;
    anchor.current = null;
    if (!a.el.isConnected) return;
    a.pane.scrollTop += a.el.getBoundingClientRect().top - a.top;
  }, [data, fill.isPending]);
  return {
    loading: fill.isPending,
    failed: fill.isError,
    near: (el, pane) => {
      if (!fill.isPending) fill.mutate({ el, pane });
    },
  };
}

/**
 * Where the rows not read yet stand. Key it by `gap.after`: a new row for
 * each page, so it is seen again when one went in.
 */
export function GapRow({ fill }: { fill: ColumnGapFill }) {
  const ref = useRef<HTMLDivElement>(null);
  const near = useRef(fill.near);
  near.current = fill.near;
  // frontend-development: not a fetch in an effect; watches the gap and asks for its next page when it comes near.
  useEffect(() => {
    const el = ref.current;
    const pane = el?.closest<HTMLElement>("[data-chat-pane]");
    if (!el || !pane || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (es) => {
        if (es.some((e) => e.isIntersecting)) near.current(el, pane);
      },
      { root: pane, rootMargin: "0px 0px 800px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      data-chat-gap=""
      className="flex h-16 items-center justify-center text-[11.5px] text-faint"
    >
      {fill.failed
        ? m.agents_load_failed()
        : fill.loading
          ? m.agents_loading_older()
          : ""}
    </div>
  );
}
